import { defineStore } from 'pinia'
import { useAccountLoginApi, useMobileLoginApi, useLogoutApi, useThirdLoginApi, useSsoLoginApi } from '@/api/auth'
import { useUserInfoApi } from '@/api/sys/user'
import cache from '@/utils/cache'
import { useAuthorityListApi } from '@/api/sys/menu'
import { Storage } from '@/utils/storage'
import constant from '@/utils/constant'
import { createDynamicWatermark } from '@/utils/watermark'
import dayjs from 'dayjs'
export const useUserStore = defineStore('userStore', {
	state: () => ({
		// 用户信息
		user: cache.getUserInfo() || {
			id: '',
			superAdmin: 0,
			username: '',
			realName: '',
			gender: 0,
			mobile: '',
			email: '',
			avatar: '',
			logoImgPath: '',
			orgName: '',
			postNameList: [],
			createTime: '',
			orgLeader: 0,
			orgId: 0
		},
		// 权限列表
		authorityList: cache.getAuthorityList(),
		// 访问token
		token: cache.getToken(),
		// 刷新token
		refreshToken: cache.getRefreshToken(),
		showPlat: cache.getShowPlat(),
		accountExpireTime: null
	}),
	actions: {
		setUser(val: any) {
			this.user = val
			// 同步到缓存
			cache.setUserInfo(val)
		},
		setToken(val: any) {
			this.token = val
			cache.setToken(val)
		},
		setRefreshToken(val: any) {
			this.refreshToken = val
			cache.setRefreshToken(val)
		},
		// 账号登录
		async accountLoginAction(loginForm: any) {
			const { data } = await useAccountLoginApi(loginForm)

			// 检查密码是否过期
			if (data.passwordExpire) {
				const error = new Error('PASSWORD_EXPIRED')
				// 将token信息附加到错误对象上，供密码修改接口使用
				;(error as any).tokenData = {
					access_token: data.access_token,
					refresh_token: data.refresh_token
				}
				throw error
			}
			this.accountExpireTime = data.accountExpireTime
			if (data.accountExpireTime) {
				const expireTime = dayjs(data.accountExpireTime)
				const currentTime = dayjs()

				if (currentTime.isAfter(expireTime)) {
					const error = new Error('ACCOUNT_EXPIRED')
					throw error
				}
			}

			if (data.secretKeyEnable !== 1) {
				this.setToken(data.access_token)
				this.setRefreshToken(data.refresh_token)
			}
			// 返回完整数据，供外部使用（如检查双因子认证）
			return data
		},
		// 手机号登录
		async mobileLoginAction(loginForm: any) {
			const { data } = await useMobileLoginApi(loginForm)
			this.setToken(data.access_token)
			this.setRefreshToken(data.refresh_token)
		},
		// 第三方登录
		async thirdLoginAction(loginForm: any) {
			const { data } = await useThirdLoginApi(loginForm)
			this.setToken(data.access_token)
			this.setRefreshToken(data.refresh_token)
		},
		// SSO 单点登录
		async ssoLoginAction(code: string) {
			const { data } = await useSsoLoginApi({ code })
			this.setToken(data.access_token)
			this.setRefreshToken(data.refresh_token)
		},
		// 获取用户信息
		async getUserInfoAction() {
			const { data } = await useUserInfoApi()
			this.setUser(data)
			// 暂时注释掉水印功能
			// if (data.username) {
			// 	createDynamicWatermark(`演示环境 - ${data.username}`)
			// }
		},
		// 获取权限
		async getAuthorityListAction() {
			const { data } = await useAuthorityListApi()
			this.authorityList = data || []
			// 同步到缓存
			cache.setAuthorityList(this.authorityList)
		},
		// 用户退出
		async logoutAction() {
			await useLogoutApi()
			localStorage.removeItem('customTitle')
			localStorage.removeItem('ai_assistant_tip_closed')
			// 移除 token
			this.setToken(null)
			this.setRefreshToken(null)
			Storage.removeItem('knowledgeBase_selectedFolder')
			// 清除用户信息缓存
			cache.removeUserInfo()
			cache.removeDictList()
			cache.removeAuthorityList()
			// 清除水印 observer
			if (window.globalWatermarkObserver) {
				window.globalWatermarkObserver.disconnect()
				window.globalWatermarkObserver = null
			}
			// 移除已有水印
			const existingWatermark = document.getElementById('system-watermark')
			if (existingWatermark) {
				existingWatermark.remove()
			}
			this.user = {
				id: '',
				superAdmin: 0,
				username: '',
				realName: '',
				gender: 0,
				mobile: '',
				email: '',
				avatar: '',
				logoImgPath: '',
				orgName: '',
				postNameList: [],
				createTime: '',
				orgLeader: 0,
				orgId: 0
			}
			this.authorityList = []
		}
	}
})
